const BASE = import.meta.env?.VITE_API_URL ?? "/api"

// Clerk token getter — registered by ClerkTokenSync on app startup.
// Returns the current Clerk session token, or null if not signed in.
type ClerkTokenGetter = () => Promise<string | null>

let _clerkTokenGetter: ClerkTokenGetter | null = null
let _clerkReady = false
let _clerkReadyWaiters: Array<() => void> = []

export const setClerkReady = (ready: boolean) => {
  _clerkReady = ready
  if (ready) {
    const waiters = _clerkReadyWaiters
    _clerkReadyWaiters = []
    for (const resolve of waiters) resolve()
  }
}

export const setClerkTokenGetter = (fn: ClerkTokenGetter | null) => {
  _clerkTokenGetter = fn
  setClerkReady(fn !== null)
}

export const isClerkTokenReady = () => _clerkReady

let _clerkReadyTimeoutMs = 3000
export const setClerkReadyTimeoutMs = (ms: number) => { _clerkReadyTimeoutMs = ms }
export const getClerkReadyTimeoutMs = () => _clerkReadyTimeoutMs

export const waitForClerkReady = (timeoutMs = _clerkReadyTimeoutMs): Promise<boolean> => {
  if (_clerkReady) return Promise.resolve(true)
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null
    const onReady = () => {
      if (timer) clearTimeout(timer)
      resolve(true)
    }
    _clerkReadyWaiters.push(onReady)
    if (timeoutMs > 0) {
      timer = setTimeout(() => {
        _clerkReadyWaiters = _clerkReadyWaiters.filter((cb) => cb !== onReady)
        resolve(false)
      }, timeoutMs)
    }
  })
}

// Legacy localStorage helpers used by tenant-switch + invite flows.
// These store short-lived bridge JWTs that the middleware verifies via the
// legacy JWT path. Keep for backward compat with tenant and invite routes.
export const TOKEN_KEY  = "snow_token"
export const getToken   = () => (typeof localStorage !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null)
export const setToken   = (t: string) => { if (typeof localStorage !== "undefined") localStorage.setItem(TOKEN_KEY, t) }
export const clearToken = () => { if (typeof localStorage !== "undefined") localStorage.removeItem(TOKEN_KEY) }

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Token selection:
  //  • D2C endpoints (/auth/d2c/*, /v1/auth/d2c/*, /v1/d2c/*) authenticate
  //    with the Clerk D2C session token. Never fall back to the clinic bridge
  //    token on these paths: d2cAuthMiddleware rejects kind="bridge" JWTs.
  //  • Clinic endpoints (/v1/*, /auth/me, /auth/quota, …) authenticate with the
  //    bridge JWT when present — a D2C doctor's Clerk token is for the *D2C*
  //    Clerk app and is rejected by clinic auth, and the bridge *cookie* is
  //    cross-site (blocked). So send the stored bridge JWT as Bearer instead.
  //  • Pure clinic users (app.snowmed.io) have no bridge token → fall through
  //    to their Clerk clinic token.
  const isD2cPath = ["/auth/d2c", "/v1/auth/d2c", "/v1/d2c"]
    .some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
  const bridgeToken = typeof localStorage !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null

  let authToken: string | null = null
  if (isD2cPath) {
    if (!_clerkReady) {
      const ready = await waitForClerkReady(_clerkReadyTimeoutMs)
      if (!ready) {
        throw Object.assign(new Error("Clerk authentication is not ready"), {
          status: 401,
          code: "CLERK_UNREADY",
        })
      }
    }
    if (_clerkTokenGetter) {
      try { authToken = await _clerkTokenGetter() } catch {}
    }
  } else if (bridgeToken) {
    authToken = bridgeToken
  } else if (_clerkTokenGetter) {
    try { authToken = await _clerkTokenGetter() } catch {}
  }
  if (!isD2cPath && !authToken) {
    authToken = bridgeToken
  }

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { "Authorization": `Bearer ${authToken}` } : {}),
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText })) as {
      error?: string; message?: string; limit?: number
    }
    const msg = err.message ?? err.error ?? "API error"
    throw Object.assign(new Error(msg), { status: res.status, limit: err.limit })
  }
  return res.json() as Promise<T>
}

export const api = {
  auth: {
    me:     () => apiFetch<AuthUser>("/auth/me"),
    quota:  () => apiFetch<{ allowed: boolean; used: number; limit: number; windowDays: number; retryAfterS?: number }>("/auth/quota"),
    logout: () => apiFetch("/auth/logout", { method: "POST" }),
  },

  // D2C auth — for summarisenow.snowmed.health Patient/Doctor surface.
  // Backed by app_users (no tenant). Distinct Clerk app (SNOW Consumer).
  authD2c: {
    me: () => apiFetch<{
      ok: true; id: string; email: string; name: string | null;
      enabledRoles: ("patient" | "doctor")[];
      mobile: string | null; mobileVerifiedAt: string | null;
      whatsappOptIn: boolean; lastLoginAt: string | null; createdAt: string;
      hasClinic?: boolean;
      clinicUserId?: string | null;
    }>("/auth/d2c/me"),
    // Called after first Clerk D2C sign-in to create the app_users row.
    provision: (role: "patient" | "doctor", name?: string, returnTo?: string) =>
      apiFetch<{ ok: boolean; id: string; email: string; enabledRoles: string[] }>("/auth/d2c/provision", {
        method: "POST",
        body:   JSON.stringify({ role, name, returnTo }),
      }),
    logout: () => apiFetch("/auth/d2c/logout", { method: "POST" }),
    addRole: (role: "patient" | "doctor") =>
      apiFetch<{ ok: boolean; enabledRoles: string[]; alreadyEnabled?: boolean }>(
        "/auth/d2c/role/add",
        { method: "POST", body: JSON.stringify({ role }) }
      ),

    // Doctor slug + profile
    getSlug: () => apiFetch<{
      ok: true; slug: string | null; clinic_or_hospital: string | null;
      name: string | null; slug_changed_at: string | null; days_until_rename: number;
    }>("/auth/d2c/slug"),
    suggestSlugs: (overrides?: { name?: string; clinic?: string }) => {
      const qs = new URLSearchParams()
      if (overrides?.name)   qs.set("name", overrides.name)
      if (overrides?.clinic) qs.set("clinic", overrides.clinic)
      const suffix = qs.toString() ? `?${qs}` : ""
      return apiFetch<{ ok: true; suggestions: Array<{ slug: string; available: boolean }> }>(
        `/auth/d2c/slug/suggest${suffix}`
      )
    },
    setSlug: (slug: string, clinic?: string) =>
      apiFetch<{ ok: boolean; slug?: string; error?: string; days_until_rename?: number }>(
        "/auth/d2c/slug",
        { method: "PATCH", body: JSON.stringify({ slug, clinic }) }
      ),
    updateProfile: (args: { name?: string; clinic_or_hospital?: string }) =>
      apiFetch<{ ok: boolean }>("/auth/d2c/profile", {
        method: "PATCH", body: JSON.stringify(args),
      }),

    // Tiered onboarding (L1 → L2)
    upgradeStatus: () => apiFetch<{
      ok: true;
      status: "level1" | "pending_review" | "approved" | "rejected";
      request: any;
      requested_at: string | null;
      reviewed_at: string | null;
      reject_reason: string | null;
      upgraded_to_user_id: string | null;
    }>("/auth/d2c/upgrade-status"),
    upgradeRequest: (args: {
      clinic_name?:    string | null;
      specialty:       string;
      reg_number:      string;
      medical_council: string;
      mobile?:         string;
    }) => apiFetch<{ ok: boolean; status?: string; error?: string }>("/auth/d2c/upgrade-request", {
      method: "POST", body: JSON.stringify(args),
    }),
    bridgeClinicSession: async () => {
      const res = await apiFetch<{ ok: boolean; user_id?: string; tenant_id?: string; token?: string; error?: string }>(
        "/auth/d2c/bridge-clinic-session", { method: "POST", body: "{}" }
      )
      // Persist the bridge JWT so clinic calls can authenticate via Bearer
      // header (the cross-site cookie is blocked by browsers). See apiFetch.
      if (res?.ok && res.token) setToken(res.token)
      return res
    },
    deskSyncToken: () => apiFetch<{ ok: boolean; token: string; expires_in_days: number }>(
      "/v1/auth/d2c/desk-sync-token"
    ),
  },

  // UC1 Knowledge Search — search synced desk extractions + summaries
  deskKnowledge: {
    search: (q: string, opts?: { limit?: number; type?: "extraction" | "summary" | "all" }) => {
      const params = new URLSearchParams({ q })
      if (opts?.limit) params.set("limit", String(opts.limit))
      if (opts?.type) params.set("type", opts.type)
      return apiFetch<{
        ok: boolean
        query: string
        extractions: Array<{
          id: string
          class_id: string
          subtype_id: string | null
          full_text: string | null
          patient_name: string | null
          patient_id: string | null
          document_name: string | null
          extracted_at: string
          rank: number
        }>
        summaries: Array<{
          id: string
          summary_type: string
          version: number
          full_text: string | null
          patient_name: string | null
          patient_id: string | null
          created_at: string
          rank: number
        }>
      }>(`/desk/knowledge/search?${params}`)
    },
  },

  // Admin doctor approval
  adminD2cDoctors: {
    list: (status: "pending_review" | "approved" | "rejected" | "level1" = "pending_review") =>
      apiFetch<{ ok: true; doctors: Array<any> }>(`/admin/doctor-approvals?status=${status}`),
    approve: (id: string) => apiFetch<{ ok: boolean; tenant_id?: string; user_id?: string; slug?: string | null }>(
      `/admin/doctor-approvals/${id}/approve`, { method: "POST", body: "{}" }
    ),
    reject: (id: string, reason: string) => apiFetch<{ ok: boolean; reason?: string }>(
      `/admin/doctor-approvals/${id}/reject`, { method: "POST", body: JSON.stringify({ reason }) }
    ),
  },

  // D2C Patient flow — uploads + summaries + family members.
  d2cPatient: {
    transcribe: async (audio: Blob, language?: string) => {
      const form = new FormData()
      form.append("audio", audio, "recording.webm")
      if (language) form.append("language", language)
      if (!_clerkReady) {
        const ready = await waitForClerkReady(_clerkReadyTimeoutMs)
        if (!ready) {
          throw Object.assign(new Error("Clerk authentication is not ready"), {
            status: 401,
            code: "CLERK_UNREADY",
          })
        }
      }
      let authToken: string | null = null
      if (_clerkTokenGetter) {
        try { authToken = await _clerkTokenGetter() } catch {}
      }
      const res = await fetch(`${BASE}/v1/d2c/transcribe`, {
        method: "POST",
        body: form,
        credentials: "include",
        headers: {
          ...(authToken ? { "Authorization": `Bearer ${authToken}` } : {}),
        },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw Object.assign(new Error(data.error ?? "transcription_failed"), { status: res.status })
      }
      return data as {
        ok: true
        transcript: string
        language_detected?: string
        confidence?: number
        duration_s?: number
      }
    },
    presignUpload: (mime: string, sizeBytes: number, filename?: string) =>
      apiFetch<{ ok: true; upload_url: string; storage_key: string; mime: string; expires_at: string }>(
        "/v1/d2c/uploads/presign",
        { method: "POST", body: JSON.stringify({ mime, size_bytes: sizeBytes, filename }) }
      ),
    createSummary: (args: {
      files: Array<{ storage_key: string; mime: string; name?: string; size_bytes?: number }>
      family_member_id?: string | null
      intent?:           "general" | "abnormal" | "questions" | "previsit" | "organise" | null
      title?:            string
      use_history_context?: boolean
      chief_complaint?:  string | null
      patient_narrative?: string | null
    }) => apiFetch<{ ok: true; id: string; created_at: string; summary: any }>(
      "/v1/d2c/summaries",
      { method: "POST", body: JSON.stringify(args) }
    ),
    listSummaries: (familyMemberId?: string) =>
      apiFetch<{ ok: true; summaries: Array<{
        id: string; title: string | null; intent: string | null; created_at: string;
        family_member_id: string | null; family_member_name: string | null;
        file_count: number; share_count: number;
        // Populated for intent='organise' only (drives the My Records vault view).
        summary_json: any | null;
      }> }>(`/v1/d2c/summaries${familyMemberId ? `?family_member_id=${familyMemberId}` : ""}`),
    getSummary: (id: string) =>
      apiFetch<{ ok: true; summary: any; shares: any[] }>(`/v1/d2c/summaries/${id}`),
    listFamilyMembers: () =>
      apiFetch<{ ok: true; members: Array<{
        id: string; display_name: string; relationship: string;
        dob: string | null; gender: string | null;
        summary_count: number; created_at: string;
      }> }>("/v1/d2c/family-members"),
    addFamilyMember: (m: { display_name: string; relationship: string; dob?: string | null; gender?: string | null }) =>
      apiFetch<{ ok: true; member: any }>("/v1/d2c/family-members", {
        method: "POST", body: JSON.stringify(m),
      }),
    deleteFamilyMember: (id: string) =>
      apiFetch<{ ok: boolean; error?: string; count?: number }>(`/v1/d2c/family-members/${id}`, {
        method: "DELETE",
      }),

    // Sharing — patient sends to doctor.
    createShare: (summaryId: string, args: {
      recipient_email:        string
      recipient_name?:        string | null
      recipient_mobile_e164?: string | null
      expires_in_days?:       number
      // Granular controls (migration 065). All optional; omitted = include everything.
      share_options?: {
        include_summary?:        boolean
        include_originals?:      boolean
        originals_order?:        "chronological" | "by_type" | "as_uploaded"
        selected_storage_keys?:  string[] | null
      }
    }) => apiFetch<{
      ok: true; id: string; token: string; share_url: string;
      expires_at: string; email_sent: boolean; email_error: string | null;
    }>(`/v1/d2c/summaries/${summaryId}/share`, {
      method: "POST", body: JSON.stringify(args),
    }),

    revokeShare: (id: string) => apiFetch<{ ok: boolean }>(
      `/v1/d2c/shares/${id}/revoke`, { method: "POST", body: "{}" }
    ),

    listShares: () => apiFetch<{ ok: true; shares: Array<{
      id: string; summary_id: string; recipient_email: string;
      recipient_name: string | null; recipient_mobile_e164: string | null;
      expires_at: string; revoked_at: string | null;
      last_viewed_at: string | null; view_count: number; created_at: string;
      summary_title: string | null;
    }> }>("/v1/d2c/shares"),

    shareQuota: () => apiFetch<{
      ok: true; limit: number; used: number; remaining: number;
      window_hours: number; at_limit: boolean;
    }>("/v1/d2c/shares/quota"),
  },

  // D2C Doctor inbox.
  d2cDoctor: {
    inbox: () => apiFetch<{ ok: true; inbox: Array<{
      id: string; summary_id: string; shared_at: string; expires_at: string;
      revoked_at: string | null; last_viewed_at: string | null; view_count: number;
      summary_title: string | null; summary_created_at: string;
      summary_json: any;
      patient_name: string | null; patient_email: string;
      family_member_name: string | null;
    }> }>("/v1/d2c/doctor/inbox"),
    getShare: (id: string) => apiFetch<{ ok: true; share: any }>(`/v1/d2c/doctor/inbox/${id}`),
  },

  // Public share view (no auth) — fetched directly from /share/:token.
  // Note: BASE prefix already handles /api routing in the client.
  d2cShare: {
    public: (token: string) => apiFetch<{
      ok: true;
      share: {
        shared_at: string; expires_at: string; recipient_name: string | null;
        patient_name: string | null; patient_email: string;
        summary_title: string | null; summary_created_at: string;
        family_member_name: string | null; summary_json: any;
        attachments?: Array<{ storage_key: string; name: string | null; mime: string | null; size_bytes: number | null; url: string }>;
        share_options?: {
          include_summary:   boolean
          include_originals: boolean
          originals_order:   "chronological" | "by_type" | "as_uploaded"
        };
      }
    }>(`/v1/d2c/public/share/${token}`),
  },

  apiKeys: {
    list:   ()             => apiFetch<{ apiKeys: ApiKey[] }>("/v1/api-keys"),
    create: (name: string, expiresInDays: number | null = null, mode: "live" | "test" = "live") =>
              apiFetch<{ id: string; key: string; prefix: string; name: string; mode: "live" | "test"; expiresAt: string | null; createdAt: string }>("/v1/api-keys", {
                method: "POST",
                body:   JSON.stringify({ name, expiresInDays, mode }),
              }),
    revoke: (id: string)   => apiFetch(`/v1/api-keys/${id}`, { method: "DELETE" }),
  },

  documents: {
    getUploadUrl: (filename: string, mimeType: string) =>
                    apiFetch<{ uploadUrl: string; documentId: string; r2Key: string }>(
                      "/v1/documents/upload-url",
                      { method: "POST", body: JSON.stringify({ filename, mimeType }) }
                    ),
    process:      (id: string, useCase: string, opts?: { isHandwritten?: boolean; researchMode?: boolean }) =>
                    apiFetch<{ jobId: string; status: string }>(`/v1/documents/${id}/process`, {
                      method: "POST",
                      body:   JSON.stringify({ useCase, ...opts }),
                    }),
    get:          (id: string)  => apiFetch<Document>(`/v1/documents/${id}`),
    extract:      (id: string)  => apiFetch<{ document_id: string; status: string }>(
                    `/v1/documents/${id}/reprocess`,
                    { method: "POST", body: JSON.stringify({}) }
                  ),
    list:         (status?: string) =>
                    apiFetch<{ documents: Document[] }>(`/v1/documents${status ? `?status=${status}` : ""}`),
    getSummary:   (id: string)  => apiFetch<Summary>(`/v1/documents/${id}/summary`),
    /**
     * On-demand thumbnail (Option B). First call renders + caches; subsequent
     * calls hit cache. Returns kind="image" for jpg/png/heic (rendered ~1024px
     * JPEG); kind="pdf" or "other" for non-images (no inline render — client
     * shows an icon card with the original-download URL).
     */
    thumb: (id: string) =>
      apiFetch<{
        id:       string
        kind:     "image" | "pdf" | "other"
        filename: string | null
        mime:     string | null
        url:      string
        bytes?:   number
      }>(`/v1/documents/${id}/thumb`),
  },

  summaries: {
    get:          (id: string)                           => apiFetch<Summary>(`/v1/summaries/${id}`),
    listForPatient: (patientId: string, useCase?: string) =>
                    apiFetch<{ summaries: Summary[] }>(
                      `/v1/patients/${patientId}/summaries${useCase ? `?useCase=${useCase}` : ""}`
                    ),
    timeline:     (patientId: string)                    =>
                    apiFetch<{ timeline: TimelineEvent[] }>(`/v1/patients/${patientId}/timeline`),
  },

  patients: {
    list:   (q?: string)    => apiFetch<{ patients: Patient[] }>(`/v1/patients${q ? `?q=${q}` : ""}`),
    get:    (id: string)    => apiFetch<Patient>(`/v1/patients/${id}`),
    create: (data: Partial<Patient>) =>
              apiFetch<Patient>("/v1/patients", { method: "POST", body: JSON.stringify(data) }),
  },

  usage: {
    get:     ()               => apiFetch<UsageStats>("/v1/usage"),
    history: (days = 30)      => apiFetch<{ history: UsageDay[] }>(`/v1/usage/history?days=${days}`),
  },

  research: {
    list:      (useCase?: string) =>
                 apiFetch<{ comparisons: Comparison[] }>(`/v1/research/comparisons${useCase ? `?useCase=${useCase}` : ""}`),
    get:       (id: string)       => apiFetch<Comparison>(`/v1/research/comparisons/${id}`),
    setWinner: (id: string, winner: string, notes?: string) =>
                 apiFetch(`/v1/research/comparisons/${id}/winner`, {
                   method: "POST",
                   body:   JSON.stringify({ winner, notes }),
                 }),
    summary:   (useCase?: string) =>
                 apiFetch<{ summary: unknown[] }>(`/v1/research/summary${useCase ? `?useCase=${useCase}` : ""}`),
  },

  intake: {
    submit: (data: { specialty: string; patient_id?: string; form_responses: Record<string, unknown> }) =>
              apiFetch<IntakeResult>("/v1/intake", { method: "POST", body: JSON.stringify(data) }),
    get:    (id: string) => apiFetch<IntakeResult>(`/v1/intake/${id}`),
  },

  intakeSessions: {
    create: (data: { specialty: string; expires_in_minutes?: number }) =>
              apiFetch<IntakeSessionCreated>("/v1/intake-sessions", { method: "POST", body: JSON.stringify(data) }),
    list:   (status?: string) =>
              apiFetch<{ sessions: IntakeSession[] }>(`/v1/intake-sessions${status ? `?status=${status}` : ""}`),
    get:    (token: string) =>
              apiFetch<IntakeSession>(`/v1/intake-sessions/${token}`),
  },

  note: {
    create: (data: { patient_id?: string; transcript?: string; document_id?: string; format?: string; specialty?: string }) =>
              apiFetch<NoteResult>("/v1/note", { method: "POST", body: JSON.stringify(data) }),
    get:    (id: string) => apiFetch<NoteResult>(`/v1/note/${id}`),
  },

  previsit: {
    queue: () => apiFetch<{ queue: PreVisitQueueItem[] }>("/v1/previsit"),
  },

  sessions: {
    list: (params?: { type?: string; status?: string }) => {
      const q = new URLSearchParams()
      if (params?.type)   q.set("type",   params.type)
      if (params?.status) q.set("status", params.status)
      const qs = q.toString()
      return apiFetch<{ sessions: Session[] }>(`/v1/sessions${qs ? `?${qs}` : ""}`)
    },
    get:    (id: string) => apiFetch<Session>(`/v1/sessions/${id}`),
    create: (data: {
      type:                  string
      specialty?:            string
      referred_to?:          string
      purpose?:              string
      consultation_type?:    string
      patient_id?:           string
      appointment_id?:       string
      language?:             string
      expires_in_minutes?:   number
      document_ids?:         string[]
      patient?:              Record<string, unknown>
      clinic?:               Record<string, unknown>
      metadata?:             Record<string, unknown>
      template_code?:        string
      intake_session_id?:    string
    }) => apiFetch<SessionCreated>("/v1/sessions", { method: "POST", body: JSON.stringify(data) }),
    // Submit endpoint is PUBLIC — keyed by token, no auth header needed.
    // Used by the doctor UI to auto-submit clinic-uploaded-docs flows where
    // there's no patient questionnaire to wait on.
    submit: (token: string, body: {
      structured_answers?: Record<string, unknown>
      narrative_text?:     string
      patient_name?:       string
      phone?:              string
      report_check?:       { document_ids: string[]; share_anyway?: boolean }
    } = {}) =>
      apiFetch<{ ok: boolean; message: string }>(`/v1/sessions/${token}/submit`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    uploadStatus: (token: string) =>
      apiFetch<{ documents: Array<{ document_id: string; state: "processing" | "read" | "failed"; uploaded_at: string | null }> }>(
        `/v1/sessions/${token}/uploads/status`,
      ),
    // Doctor review actions on completed sessions.
    patch: (id: string, body: { confirmed?: boolean; edited_result?: Record<string, unknown> | null }) =>
      apiFetch<{ id: string; confirmed_at: string | null; edited_at: string | null }>(
        `/v1/sessions/${id}`, { method: "PATCH", body: JSON.stringify(body) },
      ),
    delete: (id: string) =>
      apiFetch<{ ok: boolean; id: string }>(`/v1/sessions/${id}`, { method: "DELETE" }),
    // Submit a guided clinical-validation review. Requires user.canValidate.
    review: (id: string, body: {
      drug_check:    "ok" | "wrong" | "na" | null
      safety_check:  "ok" | "wrong" | "na" | null
      triage_check:  "ok" | "wrong" | "na" | null
      labs_check:    "ok" | "wrong" | "na" | null
      trust_rating:  number
      note?:         string
      confusing?:    string
      question_set?: string  // defaults to v1 server-side
    }) => apiFetch<{ ok: boolean; id: string; created_at: string; review_type: "selected" | "open"; question_set: string }>(
      `/v1/sessions/${id}/review`, { method: "POST", body: JSON.stringify(body) },
    ),
    // Server-side PDF (Puppeteer renderer + R2 cache). Returns a presigned
    // R2 URL the browser then GETs to download. Cached when session content
    // hasn't changed since last render.
    /**
     * Fetch a presigned PDF URL.
     *
     * @param sectionsOrInclude  Either a section list ("summary", "insights",
     *                           "trends" — any subset) or the legacy
     *                           "full"|undefined flag (kept for back-compat
     *                           with existing call sites). When a section list
     *                           is passed, the API filters the rendered PDF
     *                           accordingly and caches each combo separately.
     * @param labLayout          "tests" → flowsheet pivot (rows = tests,
     *                           columns = dates); "visits" → date-grouped
     *                           rows. Mirrors the web "Lab view" toggle so
     *                           PDF and web render the same investigations
     *                           layout. Each layout caches separately.
     */
    pdfUrl: (
      id: string,
      sectionsOrInclude?: "full" | Array<"summary" | "insights" | "trends">,
      labLayout?:        "visits" | "tests",
    ) => {
      const params: string[] = []
      if (Array.isArray(sectionsOrInclude)) {
        params.push(`sections=${sectionsOrInclude.join(",")}`)
      } else if (sectionsOrInclude === "full") {
        params.push("include=insights")
      }
      if (labLayout === "tests") params.push("layout=tests")
      const qs = params.length ? `?${params.join("&")}` : ""
      return apiFetch<{ url: string; cached: boolean; shareUrl?: string }>(`/v1/sessions/${id}/pdf${qs}`)
    },
    // Build a wa.me click-to-chat URL for the patient form. Doctor's frontend
    // opens this in a new tab; doctor's own WhatsApp delivers the message.
    waLink: (id: string, body: { phone: string; appointment_slot?: string; patient_name?: string }) =>
      apiFetch<{ wa_url: string; message: string; fallback_url: string }>(
        `/v1/sessions/${id}/wa-link`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    /**
     * List documents attached to a session (drives the file-kind tabs +
     * split-screen viewer). Returns metadata only — clients lazy-load
     * thumbnails per-document via documents.thumb().
     */
    documents: (id: string) =>
      apiFetch<{ documents: Array<{
        id:         string
        mime:       string | null
        filename:   string | null
        doc_type:   string | null
        size_bytes: number | null
        status:     string | null
        created_at: string
      }> }>(`/v1/sessions/${id}/documents`),
    /** Every attached document (patient uploads and documents attached at
     *  creation) with its read quality — no extracted content. */
    sourceDocuments: (id: string) =>
      apiFetch<{ documents: SourceDocument[] }>(`/v1/sessions/${id}/source-documents`),
    /** Who edited / confirmed the summary, and when (names, roles, times only). */
    editHistory: (id: string) =>
      apiFetch<{ entries: Array<{ action: string; at: string | null; actor: string; role: string | null }> }>(`/v1/sessions/${id}/edit-history`),
    /** Preview of a session document (thumbnail for images, original URL otherwise). */
    sourceDocumentPreview: (id: string, documentId: string) =>
      apiFetch<{ id: string; kind: "image" | "pdf" | "other"; filename: string | null; mime: string | null; url: string }>(
        `/v1/sessions/${id}/source-documents/${documentId}/preview`),
    /** Short-lived link to the original file (originals scope; the API decides). */
    sourceDocumentOriginal: (id: string, documentId: string) =>
      apiFetch<{ url: string }>(`/v1/sessions/${id}/source-documents/${documentId}/original`),
  },

  templates: {
    list:    () => apiFetch<{ templates: TenantTemplate[] }>("/v1/templates"),
    get:     (code: string) => apiFetch<TenantTemplateFull>(`/v1/templates/${code}`),
    clone:   (clone_from_code: string, overrides?: Partial<TenantTemplateFull>) =>
               apiFetch<{ id: string; code: string; kind: string }>("/v1/templates", {
                 method: "POST",
                 body:   JSON.stringify({ clone_from_code, ...overrides }),
               }),
    create:  (data: Partial<TenantTemplateFull>) =>
               apiFetch<{ id: string; code: string; kind: string }>("/v1/templates", {
                 method: "POST", body: JSON.stringify(data),
               }),
    update:  (id: string, data: Partial<TenantTemplateFull>) =>
               apiFetch(`/v1/templates/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    remove:  (id: string) => apiFetch(`/v1/templates/${id}`, { method: "DELETE" }),
  },

  webhooks: {
    get:         () => apiFetch<WebhookConfig>("/v1/me/webhook"),
    update:      (webhook_url: string) =>
                   apiFetch<{ webhook_url: string; secret: string }>("/v1/me/webhook", {
                     method: "PUT",
                     body:   JSON.stringify({ webhook_url }),
                   }),
    test:        () => apiFetch<{ delivery_id: string }>("/v1/me/webhook/test", { method: "POST" }),
    deliveries:  (limit = 50) =>
                   apiFetch<{ deliveries: WebhookDelivery[] }>(`/v1/me/webhook/deliveries?limit=${limit}`),
    replay:      (id: string) =>
                   apiFetch(`/v1/me/webhook/deliveries/${id}/replay`, { method: "POST" }),
  },

  tenants: {
    list:    ()                => apiFetch<{ tenants: Tenant[] }>("/v1/tenants"),
    create:  (name: string)    => apiFetch<Tenant>("/v1/tenants", {
                                    method: "POST",
                                    body:   JSON.stringify({ name }),
                                  }),
    rename:  (id: string, name: string) => apiFetch<Tenant>(`/v1/tenants/${id}`, {
                                    method: "PATCH",
                                    body:   JSON.stringify({ name }),
                                  }),
    update:  (id: string, data: { name?: string; brand?: TenantBrand }) =>
                   apiFetch<Tenant>(`/v1/tenants/${id}`, {
                     method: "PATCH",
                     body:   JSON.stringify(data),
                   }),
    switch:  (id: string)      => apiFetch<{ ok: boolean; token: string }>(`/v1/tenants/${id}/switch`, {
                                    method: "POST",
                                  }),

    members: {
      list:      (tenantId: string) =>
                   apiFetch<{ members: TenantMember[] }>(`/v1/tenants/${tenantId}/members`),
      setRole:   (tenantId: string, userId: string, role: TenantRole) =>
                   apiFetch<{ ok: true; role: TenantRole }>(`/v1/tenants/${tenantId}/members/${userId}`, {
                     method: "PATCH", body: JSON.stringify({ role }),
                   }),
      remove:    (tenantId: string, userId: string) =>
                   apiFetch(`/v1/tenants/${tenantId}/members/${userId}`, { method: "DELETE" }),
    },

    invites: {
      list:      (tenantId: string) =>
                   apiFetch<{ invites: TenantInvite[] }>(`/v1/tenants/${tenantId}/invites`),
      create:    (tenantId: string, email: string, role: InviteRole) =>
                   apiFetch<TenantInvite>(`/v1/tenants/${tenantId}/invites`, {
                     method: "POST", body: JSON.stringify({ email, role }),
                   }),
      revoke:    (tenantId: string, inviteId: string) =>
                   apiFetch(`/v1/tenants/${tenantId}/invites/${inviteId}`, { method: "DELETE" }),
    },
  },

  inviteLookup: (token: string) =>
    apiFetch<PublicInvite>(`/v1/invites/${token}`),
  inviteAccept: (token: string) =>
    apiFetch<{ ok: true; tenantId: string; role: TenantRole }>(`/v1/invites/${token}/accept`, { method: "POST" }),

  admin: {
    tenants:   (q?: string)  => apiFetch<{ tenants: AdminTenant[] }>(`/admin/tenants${q ? `?q=${q}` : ""}`),
    securityOverview: () => apiFetch<SecurityOverview>("/v1/admin/security/overview"),
    patchTenant: (id: string, data: { tier?: string; callsPerDay?: number }) =>
                   apiFetch(`/admin/tenants/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    extendTrial: (id: string, days: number) =>
                   apiFetch(`/admin/tenants/${id}/extend`, { method: "POST", body: JSON.stringify({ days }) }),
    compTenant:  (id: string) => apiFetch(`/admin/tenants/${id}/comp`, { method: "POST" }),
    /** Partner API keys of a tenant with their document access setting. */
    tenantKeys:  (id: string) => apiFetch<{ keys: AdminApiKey[]; services: AdminApiKeyService[]; routes: { method: string; path: string; service: string }[] }>(`/admin/tenants/${id}/api-keys`),
    setKeyScopes: (keyId: string, change: { summary_access?: boolean; document_data_access?: boolean; session_link_access?: boolean; intake_results_access?: boolean; access_profile?: "external" | "internal"; services?: Record<string, boolean> }) =>
                   apiFetch(`/admin/api-keys/${keyId}`, { method: "PATCH", body: JSON.stringify(change) }),
    uncompTenant:(id: string, days = 7) =>
                   apiFetch(`/admin/tenants/${id}/uncomp`, { method: "POST", body: JSON.stringify({ days }) }),
    clinicalRef: {
      list:    (type?: string, q?: string) =>
                 apiFetch<{ items: ClinicalRefItem[] }>(`/admin/clinical-reference?${new URLSearchParams({ ...(type ? { type } : {}), ...(q ? { q } : {}) })}`),
      create:  (data: Partial<ClinicalRefItem>) =>
                 apiFetch<{ id: string }>("/admin/clinical-reference", { method: "POST", body: JSON.stringify(data) }),
      update:  (id: string, data: Partial<ClinicalRefItem>) =>
                 apiFetch(`/admin/clinical-reference/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
      remove:  (id: string) =>
                 apiFetch(`/admin/clinical-reference/${id}`, { method: "DELETE" }),
    },
    appSettings: {
      list:   () => apiFetch<{ settings: Array<{ key: string; value: unknown; description: string | null; tenant_scope: string | null; updated_at: string; updated_by: string | null }> }>("/admin/app-settings"),
      update: (key: string, value: unknown) =>
                apiFetch<{ ok: boolean; key: string; value: unknown; updated_at: string }>(
                  `/admin/app-settings/${encodeURIComponent(key)}`,
                  { method: "PATCH", body: JSON.stringify({ value }) },
                ),
    },
    reviewQueue: () => apiFetch<{ items: ReviewItem[]; count: number }>("/admin/review-queue"),
    resolve:     (id: string) =>
                   apiFetch(`/admin/review-queue/${id}/resolve`, { method: "POST", body: JSON.stringify({}) }),

    system: {
      get:    () => apiFetch<{ settings: Record<string, unknown> }>("/admin/settings/system"),
      update: (data: Record<string, unknown>) =>
                apiFetch<{ ok: true; updated: string[] }>("/admin/settings/system", {
                  method: "PATCH", body: JSON.stringify(data),
                }),
    },

    observability: () => apiFetch<ObservabilityStatus>("/admin/observability"),

    templates: {
      list:   () => apiFetch<{ templates: AdminTemplate[] }>("/admin/templates"),
      get:    (id: string) => apiFetch<AdminTemplateFull>(`/admin/templates/${id}`),
      create: (data: Partial<AdminTemplateFull>) =>
                apiFetch<{ id: string; code: string; version: number; name: string }>("/admin/templates", {
                  method: "POST", body: JSON.stringify(data),
                }),
      update: (id: string, data: Partial<AdminTemplateFull>) =>
                apiFetch(`/admin/templates/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
      remove: (id: string) => apiFetch(`/admin/templates/${id}`, { method: "DELETE" }),
    },
    billing: {
      bundles: () => apiFetch<{ bundles: AdminBundle[] }>("/v1/admin/billing/bundles"),
      upsertBundle: (id: string, data: Partial<AdminBundle>) =>
                      apiFetch<{ ok: true; id: string }>(`/v1/admin/billing/bundles/${id}`, {
                        method: "PUT", body: JSON.stringify(data),
                      }),
      retireBundle: (id: string) =>
                      apiFetch(`/v1/admin/billing/bundles/${id}`, { method: "DELETE" }),
      caps:    (q?: string) =>
                      apiFetch<{ tenants: AdminTenantCap[] }>(
                        `/v1/admin/billing/caps${q ? `?q=${encodeURIComponent(q)}` : ""}`
                      ),
      setCap:  (tenantId: string, data: {
                  monthly_cap_paise?: number | null
                  enabled?: boolean
                  reset_day?: number
                  alert_emails?: string[]
                }) =>
                      apiFetch<{ ok: true }>(`/v1/admin/billing/caps/${tenantId}`, {
                        method: "PUT", body: JSON.stringify(data),
                      }),
    },
  },

  billing: {
    bundles:  () => apiFetch<{ bundles: PublicBundle[] }>("/v1/billing/credits/bundles"),
    balance:  () => apiFetch<{ balance: number; updated_at: string | null }>("/v1/billing/credits/balance"),
    history:  () => apiFetch<{ purchases: CreditPurchase[] }>("/v1/billing/credits/history"),
    topup:    (bundle: string) =>
                apiFetch<{
                  order_id: string; amount_paise: number; currency: string
                  key_id: string; bundle: string; expected_credits: number
                }>("/v1/billing/credits/topup", {
                  method: "POST", body: JSON.stringify({ bundle }),
                }),
  },
}

export interface TenantTemplate {
  id: string; code: string; version: number; name: string
  description: string | null; category: string; specialty: string | null
  sectionCount: number; narrativePrompt: string | null
  kind: "catalog" | "modified" | "custom"
  ownerTenantId: string | null; derivedFromId: string | null
  createdAt: string; updatedAt: string
}
export interface TenantTemplateFull extends TenantTemplate {
  sections: unknown[]
  narrativePrompt: string | null
}

export interface ObservabilityStatus {
  ts: number
  service_region: { api: string; db: string }
  integrations: {
    sentry_api: boolean; otel_api: boolean; posthog_api?: boolean
    sentry_worker: boolean; otel_worker: boolean
    sentry_web: boolean; posthog: boolean
    clarity_web: boolean; clarity_landing: boolean
    betterstack: boolean
  }
  queue: {
    pending?: number; stuck_5m?: number
    failed_24h?: number; delivered_24h?: number
    error?: string
  }
  dashboards: {
    grafana: string | null; sentry: string | null
    posthog: string | null; clarity: string | null
    betterstack: string | null
    railway_api: string | null; railway_worker: string | null
  }
  insights?: {
    posthog: {
      dau_7d?: number | null
      events_24h?: number | null
      top_events_7d?: Array<{ event: string; count: number }>
      error?: string
    }
    clarity: {
      sessions_24h?: number | null
      rage_clicks?: number | null
      dead_clicks?: number | null
      error?: string
    }
  }
}

export interface AdminTemplate {
  id: string; code: string; version: number; name: string
  description: string | null; category: string; specialty: string | null
  sectionCount: number; narrativePrompt: string | null
  isActive: boolean; kind: "catalog" | "modified" | "custom"
  ownerTenantId: string | null; derivedFromId: string | null
  createdAt: string; updatedAt: string
}
export interface AdminTemplateFull extends AdminTemplate {
  sections: unknown[]
}

// ── Types ──────────────────────────────────────────────────────────────────────
export interface TenantBrand {
  display_name?:        string
  logo_url?:            string
  primary_color?:       string
  doctor_name?:         string
  doctor_credentials?:  string
  address_line_1?:      string
  address_line_2?:      string
  phone?:               string
}

export interface AuthUser {
  id:                 string
  email:              string
  displayName:        string | null
  avatarUrl:          string | null
  authProvider:       string
  role:               string
  isPlatformAdmin?:   boolean
  tenantId:           string
  tenantName:         string
  tier:               string
  callsPerDay:        number
  subscriptionStatus: "trial" | "active" | "expired" | "comped"
  trialEndsAt:        string | null
  brand:              TenantBrand
  canValidate?:       boolean
  createdAt:          string
  tenants:            TenantSummary[]
}

export interface TenantSummary {
  id:                 string
  name:               string
  tier:               string
  subscriptionStatus: "trial" | "active" | "expired" | "comped"
  trialEndsAt:        string | null
  role:               string
}

export interface Tenant {
  id:                 string
  name:               string
  tier:               string
  callsPerDay:        number
  subscriptionStatus: "trial" | "active" | "expired" | "comped"
  trialEndsAt:        string | null
  role:               string
  createdAt:          string
  brand?: {
    display_name?:       string
    logo_url?:           string
    primary_color?:      string
    doctor_name?:        string
    doctor_credentials?: string
    address_line_1?:     string
    address_line_2?:     string
    phone?:              string
  }
}

export type TenantRole = "owner" | "developer" | "member"
export type InviteRole = "developer" | "member"

export interface TenantMember {
  id:       string
  email:    string
  role:     TenantRole
  joinedAt: string
}

export interface TenantInvite {
  id:         string
  email:      string
  role:       InviteRole
  token:      string
  inviteUrl:  string
  createdAt:  string
  expiresAt:  string
}

export interface PublicInvite {
  email:        string
  role:         InviteRole
  tenantId:     string
  tenantName:   string
  inviterEmail: string | null
  expiresAt:    string
}

export interface ApiKey {
  id:        string
  name:      string | null
  keyPrefix: string
  mode:      "live" | "test"
  lastUsed:  string | null
  isActive:  boolean
  expiresAt: string | null
  createdAt: string
}

export interface AdminApiKeyService {
  id: string
  label: string
  supported: boolean
  externalDefault: boolean
  reason: string
}

export interface AdminApiKey {
  id:                     string
  name:                   string | null
  key_prefix:             string
  mode:                   string
  is_active:              boolean
  last_used:              string | null
  created_at:             string
  summary_access:         boolean
  document_data_access:   boolean
  session_link_access:    boolean
  intake_results_access:  boolean
  access_profile:         "external" | "internal"
  services:               Record<string, boolean>
  scopes_changed_at:      string | null
  scopes_changed_by:      string | null
}

export type ReadQuality = "read" | "could_not_read" | "not_read_yet" | "stored_only" | "nothing_extracted" | "handwritten"
export interface SourceDocument {
  id:             string
  filename:       string | null
  mime:           string | null
  doc_type:       string | null
  created_at:     string | null
  quality:        ReadQuality
  needs_original: boolean
}

export interface Document {
  id:            string
  mimeType:      string
  docType:       string | null
  isHandwritten: boolean
  status:        string
  ocrConfidence: string | null
  ocrEngine:     string | null
  createdAt:     string
}

export interface Summary {
  id:                 string
  patientId:          string | null
  documentId:         string | null
  useCase:            string
  content:            Record<string, unknown>
  requiresReview:     boolean
  overallConfidence:  number | null
  model:              string | null
  layersUsed:         string[]
  latencyMs:          number | null
  version:            number
  createdAt:          string
  patientName?:       string | null
}

export interface TimelineEvent {
  id:              string
  useCase:         string
  createdAt:       string
  chiefComplaint:  string | null
  vitals:          Record<string, unknown> | null
  medications:     unknown[]
  narrative:       string | null
  requiresReview:  boolean
  docType:         string | null
}

export interface Patient {
  id:        string
  name:      string
  dob:       string | null
  gender:    string | null
  phone:     string | null
  abhaId:    string | null
  uhid:      string | null
  createdAt: string
}

export interface UsageStats {
  today:    { calls: number; llmCalls: number; limit: number }
  month:    { calls: number }
  tier:     string
  documents: Record<string, number>
  useCases:  Record<string, number>
}

export interface UsageDay {
  date:         string
  totalCalls:   number
  llmCalls:     number
  avgLatencyMs: number
}

export interface Comparison {
  id:          string
  documentId:  string | null
  useCase:     string
  runAt:       string
  models:      unknown[]
  winner:      string | null
  notes:       string | null
  modelCount:  number
}

export interface AdminTenant {
  id:                 string
  name:               string
  tier:               string
  callsPerDay:        number
  webhookUrl:         string | null
  createdAt:          string
  userCount:          number
  documentCount:      number
  subscriptionStatus: "trial" | "active" | "expired" | "comped"
  trialEndsAt:        string | null
}

export interface ClinicalRefItem {
  id:               string
  termType:         string
  localName:        string
  standardName:     string | null
  snomedCode:       string | null
  loincCode:        string | null
  rxnormCode:       string | null
  icd10Code:        string | null
  normalRangeLow:   number | null
  normalRangeHigh:  number | null
  unit:             string | null
  genderSpecific:   string | null
  category:         string | null
  notes:            string | null
}

export interface ReviewItem {
  id:                 string
  tenantId:           string
  useCase:            string
  overallConfidence:  number
  layersUsed:         string[]
  createdAt:          string
  tenantName:         string
  patientName:        string | null
  ocrEngine:          string | null
  docType:            string | null
}

export interface IntakeResult {
  intake_id:         string
  document_id:       string
  triage_tier:       "routine" | "priority" | "urgent"
  triage_reason:     string | null
  flagged_items:     string[]
  pre_consult_brief: string | null
  content:           Record<string, unknown>
  model:             string
  latency_ms:        number
  created_at:        string
}

export interface IntakeSessionCreated {
  session_id:  string
  token:       string
  intake_url:  string
  specialty:   string
  expires_at:  string
  created_at:  string
}

export interface IntakeSession {
  id:           string
  token:        string
  specialty:    string
  patient_id:   string | null
  status:       "pending" | "submitted" | "processing" | "completed" | "expired"
  triage_tier:  string | null
  patient_name: string | null
  phone:        string | null
  result:       Record<string, unknown> | null
  summary_id:   string | null
  expires_at:   string
  submitted_at: string | null
  completed_at: string | null
  created_at:   string
}

export interface NoteResult {
  note_id:                string
  format:                 "SOAP" | "DAP" | "BIRP"
  content:                Record<string, unknown>
  icd_suggestions:        Array<{ code: string; description: string; confidence: string }>
  medications_prescribed: Array<{ drug: string; dose: string; frequency: string; duration: string }>
  model:                  string
  latency_ms:             number
  created_at:             string
}

export interface Session {
  id:               string
  /** Patient's own answers, verbatim (clinic users only). */
  patient_answers?: Array<{ label: string; answer: string }>
  token:            string
  type:             string
  specialty:        string | null
  referred_to:      string | null
  purpose:          string | null
  consultation_type: string | null
  patient_id:       string | null
  appointment_id:   string | null
  patient_name:     string | null
  phone:            string | null
  status:           "pending" | "active" | "submitted" | "processing" | "completed" | "failed"
  error_message?:   string | null
  result:           Record<string, unknown> | null
  /** Partial JSON written by the worker as the LLM streams. Cleared once status flips to completed/failed. */
  streaming_content?: string | null
  edited_result?:   Record<string, unknown> | null
  summary_id:       string | null
  document_id:      string | null
  patient_info?:    Record<string, unknown> | null
  clinic_info?:     Record<string, unknown> | null
  session_url?:     string
  confirmed_at?:    string | null
  confirmed_by?:    string | null
  edited_at?:       string | null
  edited_by?:       string | null
  doctor_user_id?:  string | null
  expires_at:       string
  submitted_at:     string | null
  completed_at:     string | null
  created_at:       string
}

export interface SessionCreated {
  session_id:   string
  token:        string
  type:         string
  specialty:    string | null
  session_url:  string
  expires_at:   string
  created_at:   string
  ui_type:      "patient" | "clinician"
}

export interface WebhookConfig {
  webhook_url:           string | null
  last_delivery:         string | null
  recent_failure_count:  number
}

export interface WebhookDelivery {
  id:                  string
  event:               string
  url:                 string
  /** Lifecycle state — pending | in_flight | delivered | dead. */
  status:              "pending" | "in_flight" | "delivered" | "dead"
  /** Last HTTP status code from the most recent delivery attempt. Null until first attempt. */
  status_code:         number | null
  latency_ms:          number | null
  attempts:            number
  error:               string | null
  created_at:          string
  delivered_at?:       string | null
  next_retry_at?:      string | null
  last_attempted_at?:  string | null
  tenant_id?:          string
  tenant_name?:        string
}

export interface SecurityOverview {
  deliveries:           WebhookDelivery[]
  signature_failures:   Array<{ id: string; tenant_id: string; tenant_name: string; reason: string; created_at: string }>
  secret_rotations:     Array<{ id: string; tenant_id: string; tenant_name: string; actor_email: string | null; created_at: string }>
  presign_access:       Array<{ id: string; tenant_id: string; tenant_name: string; user_email: string | null; document_id: string; created_at: string }>
}

export interface PreVisitQueueItem {
  patient: {
    id:        string
    name:      string
    dob:       string | null
    gender:    string | null
    phone:     string | null
    abhaId:    string | null
    uhid:      string | null
    createdAt: string
  }
  summary: {
    id:             string
    content:        Record<string, unknown>
    model:          string | null
    layersUsed:     string[]
    latencyMs:      number | null
    requiresReview: boolean
    createdAt:      string
  } | null
  latestDoc: {
    status:     string
    docType:    string | null
    uploadedAt: string
  } | null
  status: "ready" | "review_needed" | "processing" | "awaiting_summary" | "no_records"
}

// ── Billing — credit packs (SNOW Platform / API tenants) ─────────────────────

export interface PublicBundle {
  id:               string
  amount_paise:     number
  amount_display:   string
  base_credits:     number
  bonus_credits:    number
  bonus_pct:        number
  total_credits:    number
  effective_paise:  number
  label:            string | null
  description:      string | null
}

export interface CreditPurchase {
  id:                 string
  amount_paise:       number
  base_credits:       number
  bonus_credits:      number
  total_credits:      number
  bundle:             string
  razorpay_order_id:  string
  razorpay_payment_id: string | null
  status:             "pending" | "paid" | "failed" | "refunded"
  created_at:         string
  paid_at:            string | null
}

export interface AdminBundle {
  id:               string
  amount_paise:     number
  bonus_pct:        number
  per_call_paise:   number
  label:            string | null
  description:      string | null
  display_order:    number
  active:           boolean
  razorpay_item_id: string | null
  created_at?:      string
  updated_at?:      string
}

export interface AdminTenantCap {
  tenant_id:          string
  tenant_name:        string
  balance:            number | null
  month_credits_used: number | null
  period_start:       string | null
  monthly_cap_paise:  number | null
  enabled:            boolean | null
  reset_day:          number | null
  alert_state:        Record<string, string | null> | null
  alert_emails:       string[] | null
  cap_updated_at:     string | null
}
